import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Textarea } from '@/components/ui';
import { ApiError } from '@/lib/api/client';
import type { Merchant, CreateMerchantParams, UpdateMerchantParams } from '@/types/merchant';

interface MerchantFormProps {
  mode: 'create' | 'edit';
  initialData?: Merchant;
  onSubmit: (data: CreateMerchantParams | UpdateMerchantParams) => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: Error | null;
}

const MERCHANT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{2,48}[a-z0-9]$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const IQ_CUSTOMER_ID_PATTERN = /^\d{4,6}$/;

export function MerchantForm({ mode, initialData, onSubmit, onCancel, isLoading = false, error }: MerchantFormProps) {
  const { t } = useTranslation('merchants');
  const isEditMode = mode === 'edit';

  const [merchantId, setMerchantId] = useState('');
  const [name, setName] = useState('');
  const [internetqCustomerId, setInternetqCustomerId] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [incidentEmail, setIncidentEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setMerchantId(initialData.merchantId);
      setName(initialData.name);
      setContactEmail(initialData.contactEmail ?? '');
      setIncidentEmail(initialData.incidentEmail ?? '');
      setInternetqCustomerId(initialData.internetqCustomerId ?? '');
      // contactName + notes not yet in Facade GET response (pending Facade-Team fix)
      setContactName(((initialData as unknown) as Record<string, unknown>).contactName as string ?? '');
      setNotes(((initialData as unknown) as Record<string, unknown>).notes as string ?? '');
    }
  }, [initialData]);

  // Map API errors to field-level errors
  useEffect(() => {
    if (error instanceof ApiError) {
      const fieldErrors: Record<string, string> = {};
      if (error.code === 'DUPLICATE_MERCHANT_ID') {
        fieldErrors.merchantId = error.message;
      } else if (error.code === 'INVALID_MERCHANT_ID') {
        fieldErrors.merchantId = error.message;
      } else if (error.code === 'INVALID_PARAMETERS' && error.details) {
        for (const detail of error.details) {
          fieldErrors[detail.field] = detail.message;
        }
      }
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
    }
  }, [error]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!isEditMode) {
      if (!merchantId.trim()) {
        newErrors.merchantId = t('form.validation.merchantIdRequired');
      } else if (!MERCHANT_ID_PATTERN.test(merchantId)) {
        newErrors.merchantId = t('form.validation.merchantIdPattern');
      }

      if (!internetqCustomerId.trim()) {
        newErrors.internetqCustomerId = t('form.validation.internetqCustomerIdRequired');
      } else if (!IQ_CUSTOMER_ID_PATTERN.test(internetqCustomerId)) {
        newErrors.internetqCustomerId = t('form.validation.internetqCustomerIdPattern');
      }
    }

    if (!name.trim()) {
      newErrors.name = t('form.validation.nameRequired');
    } else if (name.length > 100) {
      newErrors.name = t('form.validation.nameMaxLength');
    }

    if (!contactName.trim()) {
      newErrors.contactName = t('form.validation.contactNameRequired');
    } else if (contactName.length > 200) {
      newErrors.contactName = t('form.validation.contactNameMaxLength');
    }

    if (!contactEmail.trim()) {
      newErrors.contactEmail = t('form.validation.invalidEmail');
    } else if (!EMAIL_PATTERN.test(contactEmail)) {
      newErrors.contactEmail = t('form.validation.invalidEmail');
    }

    if (!isEditMode) {
      if (!incidentEmail.trim()) {
        newErrors.incidentEmail = t('form.validation.incidentEmailRequired');
      } else if (!EMAIL_PATTERN.test(incidentEmail)) {
        newErrors.incidentEmail = t('form.validation.invalidEmail');
      }
    } else {
      if (incidentEmail && !EMAIL_PATTERN.test(incidentEmail)) {
        newErrors.incidentEmail = t('form.validation.invalidEmail');
      }
    }

    if (notes.length > 2000) {
      newErrors.notes = t('form.validation.notesMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearError = (field: string) => {
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (isEditMode) {
      const data: UpdateMerchantParams = {
        name,
        contactEmail: contactEmail || null,
        incidentEmail: incidentEmail || null,
        contactName,
        notes: notes || null,
      };
      onSubmit(data);
    } else {
      const data: CreateMerchantParams = {
        merchantId,
        name,
        internetqCustomerId,
        contactName,
        contactEmail,
        incidentEmail,
        ...(notes && { notes }),
      };
      onSubmit(data);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {/* merchantId - only in create mode */}
        {!isEditMode && (
          <div className="space-y-2">
            <Label htmlFor="merchantId">
              {t('form.label.merchantId')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="merchantId"
              placeholder={t('form.placeholder.merchantId')}
              value={merchantId}
              onChange={(e) => { setMerchantId(e.target.value); clearError('merchantId'); }}
              className={errors.merchantId ? 'border-destructive' : ''}
            />
            {errors.merchantId && <p className="text-[13px] text-destructive">{errors.merchantId}</p>}
          </div>
        )}

        {/* name */}
        <div className="space-y-2">
          <Label htmlFor="name">
            {t('form.label.name')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name"
            placeholder={t('form.placeholder.name')}
            value={name}
            onChange={(e) => { setName(e.target.value); clearError('name'); }}
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && <p className="text-[13px] text-destructive">{errors.name}</p>}
        </div>

        {/* internetqCustomerId - only in create mode (immutable) */}
        {!isEditMode && (
          <div className="space-y-2">
            <Label htmlFor="internetqCustomerId">
              {t('form.label.internetqCustomerId')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="internetqCustomerId"
              placeholder={t('form.placeholder.internetqCustomerId')}
              value={internetqCustomerId}
              onChange={(e) => { setInternetqCustomerId(e.target.value); clearError('internetqCustomerId'); }}
              className={errors.internetqCustomerId ? 'border-destructive' : ''}
            />
            {errors.internetqCustomerId && <p className="text-[13px] text-destructive">{errors.internetqCustomerId}</p>}
          </div>
        )}

        {/* contactName */}
        <div className="space-y-2">
          <Label htmlFor="contactName">
            {t('form.label.contactName')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="contactName"
            placeholder={t('form.placeholder.contactName')}
            value={contactName}
            onChange={(e) => { setContactName(e.target.value); clearError('contactName'); }}
            className={errors.contactName ? 'border-destructive' : ''}
          />
          {errors.contactName && <p className="text-[13px] text-destructive">{errors.contactName}</p>}
        </div>

        {/* contactEmail */}
        <div className="space-y-2">
          <Label htmlFor="contactEmail">
            {t('form.label.contactEmail')} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="contactEmail"
            type="email"
            placeholder={t('form.placeholder.email')}
            value={contactEmail}
            onChange={(e) => { setContactEmail(e.target.value); clearError('contactEmail'); }}
            className={errors.contactEmail ? 'border-destructive' : ''}
          />
          {errors.contactEmail && <p className="text-[13px] text-destructive">{errors.contactEmail}</p>}
        </div>

        {/* incidentEmail */}
        <div className="space-y-2">
          <Label htmlFor="incidentEmail">
            {t('form.label.incidentEmail')} {!isEditMode && <span className="text-destructive">*</span>}
          </Label>
          <Input
            id="incidentEmail"
            type="email"
            placeholder={t('form.placeholder.incidentEmail')}
            value={incidentEmail}
            onChange={(e) => { setIncidentEmail(e.target.value); clearError('incidentEmail'); }}
            className={errors.incidentEmail ? 'border-destructive' : ''}
          />
          {errors.incidentEmail && <p className="text-[13px] text-destructive">{errors.incidentEmail}</p>}
        </div>

        {/* notes (optional) */}
        <div className="space-y-2">
          <Label htmlFor="notes">{t('form.label.notes')}</Label>
          <Textarea
            id="notes"
            placeholder={t('form.placeholder.notes')}
            value={notes}
            onChange={(e) => { setNotes(e.target.value); clearError('notes'); }}
            maxLength={2000}
            rows={3}
            className={errors.notes ? 'border-destructive' : ''}
          />
          {notes.length > 0 && (
            <p className="text-[12px] text-muted-foreground">{notes.length}/2000</p>
          )}
          {errors.notes && <p className="text-[13px] text-destructive">{errors.notes}</p>}
        </div>

      </div>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          {t('form.cancel')}
        </Button>
        <Button type="submit" loading={isLoading}>
          {isEditMode ? t('form.submit') : t('form.create')}
        </Button>
      </div>
    </form>
  );
}
